import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, roundedPoly, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

const CHITIN = 0x8a5a30; // burnt-bronze plating (magma flavor)
const PLATE = 0xd8cdb4; // pale armor face
const CORE = 0xd92b2b;

/**
 * I1 Centipede parts — docs/02. Armored wedge head (frame 0
 * normal / 1 enraged) + diamond body segment with a red core and side
 * turret nubs (the nubs fire perpendicular volleys). Nose points +X.
 */
export const bossCentipedeHead: ProceduralDef = {
  key: 'boss/centipede/head',
  width: 84,
  height: 84,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const enraged = frame === 1;

    // Armored wedge — blunt plow front
    roundedPoly(
      ctx,
      [
        [c + 34, c],
        [c + 20, c - 24],
        [c - 16, c - 28],
        [c - 30, c - 12],
        [c - 30, c + 12],
        [c - 16, c + 28],
        [c + 20, c + 24],
      ],
      9,
    );
    fillOutlined(ctx, PLATE, 6);

    // Face plate seam + rivets
    ctx.strokeStyle = css(shade(CHITIN, 0.8));
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(c + 24, c - 14);
    ctx.lineTo(c + 24, c + 14);
    ctx.stroke();
    ctx.fillStyle = css(CHITIN);
    for (const [rx, ry] of [[-18, -16], [-18, 16], [4, -20], [4, 20]] as const) {
      circle(ctx, c + rx, c + ry, 3.5);
      ctx.fill();
    }

    // Mandibles
    for (const side of [-1, 1] as const) {
      roundedPoly(
        ctx,
        [[c + 30, c + side * 14], [c + 46, c + side * 24], [c + 32, c + side * 26]],
        3,
      );
      fillOutlined(ctx, CHITIN, 4);
    }

    // Eye core — bigger and blazing when enraged
    circle(ctx, c + 4, c, enraged ? 12 : 9);
    fillOutlined(ctx, enraged ? COLORS.dangerRed : COLORS.dangerOrange, 4);
    circle(ctx, c + 7, c - 3, 3);
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.fill();
    if (enraged) {
      circle(ctx, c + 4, c, 18);
      ctx.fillStyle = 'rgba(217, 43, 43, 0.22)';
      ctx.fill();
    }
    void h;
  },
};

/** Diamond body segment — pale plate, red core, side turret nubs. */
export const bossCentipedeSegment: ProceduralDef = {
  key: 'boss/centipede/segment',
  width: 64,
  height: 64,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    roundedPoly(
      ctx,
      [[c + 24, c], [c, c - 22], [c - 24, c], [c, c + 22]],
      7,
    );
    fillOutlined(ctx, PLATE, 5.5);

    // Side turret nubs (top/bottom in travel space)
    for (const side of [-1, 1] as const) {
      roundedPoly(
        ctx,
        [[c - 5, c + side * 20], [c + 5, c + side * 20], [c + 3, c + side * 29], [c - 3, c + side * 29]],
        2,
      );
      fillOutlined(ctx, CHITIN, 3.5);
      circle(ctx, c, c + side * 29, 3);
      ctx.fillStyle = css(CORE);
      ctx.fill();
    }

    // Red core
    circle(ctx, c, c, 9);
    fillOutlined(ctx, CORE, 4);
    circle(ctx, c + 2.5, c - 2.5, 2.5);
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fill();
    void h;
  },
};
